/**
 * useBoardDisplay — manages sort, grouping, and field visibility state
 * via URL search params for deep-linking. Settings are encoded as query params:
 *   ?sort=priority:asc,title:desc&group=status&fields=id,type,priority,assignee
 * Defaults are used when params are absent (no URL clutter for default state).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type DisplayConfig,
  type SortRule,
  type SortField,
  type SortDirection,
  type CardField,
  type GroupField,
  DEFAULT_DISPLAY_CONFIG,
  ALL_CARD_FIELDS,
} from '../components/work/shared/displayTypes';
import { type Issue } from '../api/work';
import { calculateTaskProgress } from '../components/work/shared/utils';

// ── URL param keys ──
const SORT_PARAM = 'sort';
const GROUP_PARAM = 'group';
const FIELDS_PARAM = 'fields';

// ── Valid values for validation ──
const VALID_SORT_FIELDS = new Set<string>([
  'manual', 'priority', 'created', 'updated', 'due_date', 'title', 'type', 'story_points', 'status', 'completion',
]);
const VALID_GROUP_FIELDS = new Set<string>(['status', 'priority', 'type', 'assignee', 'none']);
const VALID_CARD_FIELDS = new Set<string>(ALL_CARD_FIELDS);

// ── Parse URL params → config ──
// URL-safe delimiters: `-` separates field from direction, `.` separates multiple items.
// This avoids URLSearchParams encoding `:` as %3A and `,` as %2C.
// Example: ?sort=priority-asc.title-desc&group=status&fields=id.type.priority

function parseSortRules(param: string | null): SortRule[] | null {
  if (!param) return null;
  const rules: SortRule[] = [];
  for (const part of param.split('.')) {
    // Try new format (field-dir) first, fallback to legacy (field:dir)
    const sepIdx = part.lastIndexOf('-');
    let field: string, dir: string | undefined;
    if (sepIdx > 0 && (part.slice(sepIdx + 1) === 'asc' || part.slice(sepIdx + 1) === 'desc')) {
      field = part.slice(0, sepIdx);
      dir = part.slice(sepIdx + 1);
    } else if (part.includes(':')) {
      [field, dir] = part.split(':');
    } else {
      field = part;
    }
    if (VALID_SORT_FIELDS.has(field)) {
      rules.push({
        field: field as SortField,
        direction: (dir === 'desc' ? 'desc' : 'asc') as SortDirection,
      });
    }
  }
  return rules.length > 0 ? rules : null;
}

function parseGroupBy(param: string | null): GroupField | null {
  if (!param) return null;
  return VALID_GROUP_FIELDS.has(param) ? (param as GroupField) : null;
}

function parseVisibleFields(param: string | null): CardField[] | null {
  if (!param) return null;
  // Support both `.` (new) and `,` (legacy) separators
  const fields = param.split(/[.,]/).filter((f) => VALID_CARD_FIELDS.has(f)) as CardField[];
  return fields.length > 0 ? fields : null;
}

function configFromParams(searchParams: URLSearchParams): DisplayConfig {
  const sortRules = parseSortRules(searchParams.get(SORT_PARAM)) ?? DEFAULT_DISPLAY_CONFIG.sortRules;
  const groupBy = parseGroupBy(searchParams.get(GROUP_PARAM)) ?? DEFAULT_DISPLAY_CONFIG.groupBy;
  const visibleFields = parseVisibleFields(searchParams.get(FIELDS_PARAM)) ?? DEFAULT_DISPLAY_CONFIG.visibleFields;
  return { sortRules, groupBy, visibleFields };
}

// ── Serialize config → URL params (omit defaults to keep URL clean) ──
// Uses `-` and `.` delimiters which URLSearchParams does NOT encode.

function serializeSortRules(rules: SortRule[]): string | null {
  if (rules.length === 1 && rules[0].field === 'manual') return null; // default
  return rules.map((r) => `${r.field}-${r.direction}`).join('.');
}

function serializeGroupBy(groupBy: GroupField): string | null {
  if (groupBy === DEFAULT_DISPLAY_CONFIG.groupBy) return null; // default
  return groupBy;
}

function serializeVisibleFields(fields: CardField[]): string | null {
  const defaultSet = new Set(DEFAULT_DISPLAY_CONFIG.visibleFields);
  const currentSet = new Set(fields);
  if (defaultSet.size === currentSet.size && [...defaultSet].every((f) => currentSet.has(f))) return null;
  return fields.join('.');
}

// ── Priority sort weights ──
const PRIORITY_WEIGHT: Record<string, number> = {
  critical: 0, high: 1, medium: 2, low: 3,
};

const TYPE_WEIGHT: Record<string, number> = {
  epic: 0, story: 1, task: 2, bug: 3, chore: 4,
};

/** Compare two issues by a single sort rule. Returns negative/zero/positive.
 *  allIssues is needed for 'completion' sort (child progress calculation). */
function compareByRule(a: Issue, b: Issue, rule: SortRule, allIssues?: Issue[]): number {
  const dir = rule.direction === 'asc' ? 1 : -1;

  switch (rule.field) {
    case 'priority':
      return dir * ((PRIORITY_WEIGHT[a.priority] ?? 99) - (PRIORITY_WEIGHT[b.priority] ?? 99));
    case 'type':
      return dir * ((TYPE_WEIGHT[a.type] ?? 99) - (TYPE_WEIGHT[b.type] ?? 99));
    case 'title':
      return dir * a.title.localeCompare(b.title);
    case 'status':
      return dir * (a.status || '').localeCompare(b.status || '');
    case 'created':
      return dir * ((a.metadata?.created_at || '').localeCompare(b.metadata?.created_at || ''));
    case 'updated':
      return dir * ((a.metadata?.updated_at || '').localeCompare(b.metadata?.updated_at || ''));
    case 'due_date': {
      const ad = a.due_date || '';
      const bd = b.due_date || '';
      if (!ad && !bd) return 0;
      if (!ad) return 1;  // no date → end
      if (!bd) return -1;
      return dir * ad.localeCompare(bd);
    }
    case 'story_points': {
      const ap = a.story_points ?? -1;
      const bp = b.story_points ?? -1;
      return dir * (ap - bp);
    }
    case 'completion': {
      const issuesList = allIssues || [];
      const aChildren = issuesList.filter(i => i.parent_id === a.id || i.epic_id === a.id).length;
      const bChildren = issuesList.filter(i => i.parent_id === b.id || i.epic_id === b.id).length;
      // Items with children always sort before childless items (regardless of direction)
      if ((aChildren > 0) !== (bChildren > 0)) return aChildren > 0 ? -1 : 1;
      const ap = calculateTaskProgress(a, issuesList);
      const bp = calculateTaskProgress(b, issuesList);
      return dir * (ap - bp);
    }
    case 'manual':
    default:
      return 0; // manual = keep original order
  }
}

/** Sort an array of issues using the given rules (multi-level). */
export function sortIssues(issues: Issue[], rules: SortRule[]): Issue[] {
  // If only manual sort, return original order
  if (rules.length === 0 || (rules.length === 1 && rules[0].field === 'manual')) {
    return issues;
  }

  return [...issues].sort((a, b) => {
    for (const rule of rules) {
      const cmp = compareByRule(a, b, rule, issues);
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
}

/** Sort issue IDs within a column using the sort rules. */
export function sortIssueIds(
  issueIds: string[],
  issues: Record<string, Issue>,
  rules: SortRule[]
): string[] {
  if (rules.length === 0 || (rules.length === 1 && rules[0].field === 'manual')) {
    return issueIds;
  }

  const allIssues = Object.values(issues);

  return [...issueIds].sort((aId, bId) => {
    const a = issues[aId];
    const b = issues[bId];
    if (!a || !b) return 0;
    for (const rule of rules) {
      const cmp = compareByRule(a, b, rule, allIssues);
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
}

export interface UseBoardDisplayReturn {
  config: DisplayConfig;
  // Sort
  sortRules: SortRule[];
  addSortRule: (field: SortField, direction?: SortDirection) => void;
  removeSortRule: (index: number) => void;
  updateSortRule: (index: number, updates: Partial<SortRule>) => void;
  clearSort: () => void;
  isManualSort: boolean;
  // Fields
  visibleFields: CardField[];
  toggleField: (field: CardField) => void;
  isFieldVisible: (field: CardField) => boolean;
  // Grouping
  groupBy: GroupField;
  setGroupBy: (field: GroupField) => void;
  // Reset
  resetToDefaults: () => void;
  // Whether any non-default settings are active
  hasCustomSettings: boolean;
}

export function useBoardDisplay(
  _projectId: string,
  _boardId: string,
): UseBoardDisplayReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive config from current URL params
  const config = useMemo(() => configFromParams(searchParams), [searchParams]);

  // Update URL params when config changes — replaces history entry to avoid back-button spam
  const updateConfig = useCallback(
    (updater: (prev: DisplayConfig) => DisplayConfig) => {
      setSearchParams((prev) => {
        const currentConfig = configFromParams(prev);
        const next = updater(currentConfig);
        const params = new URLSearchParams(prev);

        // Sort
        const sortStr = serializeSortRules(next.sortRules);
        if (sortStr) params.set(SORT_PARAM, sortStr);
        else params.delete(SORT_PARAM);

        // Group
        const groupStr = serializeGroupBy(next.groupBy);
        if (groupStr) params.set(GROUP_PARAM, groupStr);
        else params.delete(GROUP_PARAM);

        // Fields
        const fieldsStr = serializeVisibleFields(next.visibleFields);
        if (fieldsStr) params.set(FIELDS_PARAM, fieldsStr);
        else params.delete(FIELDS_PARAM);

        return params;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // ── Sort ──

  const addSortRule = useCallback(
    (field: SortField, direction: SortDirection = 'asc') => {
      updateConfig((prev) => {
        if (field === 'manual') {
          return { ...prev, sortRules: [{ field: 'manual', direction: 'asc' }] };
        }
        const filtered = prev.sortRules.filter((r) => r.field !== 'manual' && r.field !== field);
        return { ...prev, sortRules: [...filtered, { field, direction }] };
      });
    },
    [updateConfig]
  );

  const removeSortRule = useCallback(
    (index: number) => {
      updateConfig((prev) => {
        const next = prev.sortRules.filter((_, i) => i !== index);
        if (next.length === 0) return { ...prev, sortRules: [{ field: 'manual', direction: 'asc' }] };
        return { ...prev, sortRules: next };
      });
    },
    [updateConfig]
  );

  const updateSortRule = useCallback(
    (index: number, updates: Partial<SortRule>) => {
      updateConfig((prev) => {
        const next = [...prev.sortRules];
        next[index] = { ...next[index], ...updates };
        return { ...prev, sortRules: next };
      });
    },
    [updateConfig]
  );

  const clearSort = useCallback(() => {
    updateConfig((prev) => ({ ...prev, sortRules: [{ field: 'manual', direction: 'asc' }] }));
  }, [updateConfig]);

  const isManualSort = config.sortRules.length === 1 && config.sortRules[0].field === 'manual';

  // ── Fields ──

  const toggleField = useCallback(
    (field: CardField) => {
      updateConfig((prev) => {
        const has = prev.visibleFields.includes(field);
        const next = has
          ? prev.visibleFields.filter((f) => f !== field)
          : [...prev.visibleFields, field];
        return { ...prev, visibleFields: next };
      });
    },
    [updateConfig]
  );

  const isFieldVisible = useCallback(
    (field: CardField) => config.visibleFields.includes(field),
    [config.visibleFields]
  );

  // ── Grouping ──

  const setGroupBy = useCallback(
    (field: GroupField) => {
      updateConfig((prev) => ({ ...prev, groupBy: field }));
    },
    [updateConfig]
  );

  // ── Reset ──

  const resetToDefaults = useCallback(() => {
    updateConfig(() => ({ ...DEFAULT_DISPLAY_CONFIG }));
  }, [updateConfig]);

  const hasCustomSettings = useMemo(() => {
    const d = DEFAULT_DISPLAY_CONFIG;
    if (config.sortRules.length !== d.sortRules.length) return true;
    if (config.sortRules[0]?.field !== d.sortRules[0]?.field) return true;
    if (config.visibleFields.length !== d.visibleFields.length) return true;
    if (config.groupBy !== d.groupBy) return true;
    return false;
  }, [config]);

  return {
    config,
    sortRules: config.sortRules,
    addSortRule,
    removeSortRule,
    updateSortRule,
    clearSort,
    isManualSort,
    visibleFields: config.visibleFields,
    toggleField,
    isFieldVisible,
    groupBy: config.groupBy,
    setGroupBy,
    resetToDefaults,
    hasCustomSettings,
  };
}
